import type { ReactNode } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { SessionEnded } from "@client/components/session-ended";
import { LobbyScreen } from "@client/features/lobby/lobby-screen";
import { RoundResults } from "@client/features/results/round-results";
import { PlayerGameScreen } from "@client/features/game/player-game-screen";
import { usePlayerSession } from "@client/features/session/use-player-session";

export function PlayerRoute(): ReactNode {
  const { joinCode = "new" } = useParams();
  return <PlayerRoom key={joinCode.toUpperCase()} normalizedCode={joinCode.toUpperCase()} />;
}

function PlayerRoom({ normalizedCode }: { normalizedCode: string }): ReactNode {
  const navigate = useNavigate();
  const {
    projection,
    displayCode,
    currentJoinCode,
    busy,
    error,
    connectionStatus,
    connected,
    sendCommand,
    hasCredential,
    createRoom,
    joinRoom,
  } = usePlayerSession(normalizedCode, (code) => {
    void navigate(`/room/${code}`, { replace: true });
  });
  const publicState = projection?.public;
  const self = projection?.self;
  if (connectionStatus === "expired") {
    return (
      <SessionEnded
        expired
        onLeave={() => {
          void navigate("/", { replace: true });
        }}
      />
    );
  }
  if (connectionStatus === "invalid") {
    return (
      <LobbyScreen
        joinCode={normalizedCode}
        busy={busy}
        error={error ?? "This saved seat is no longer valid. Join again to continue."}
        onJoinRoom={joinRoom}
      />
    );
  }

  if (projection && publicState && self) {
    if (["LOBBY", "BETWEEN_ROUNDS", "COMPLETED"].includes(publicState.phase)) {
      return (
        <LobbyScreen
          projection={projection}
          joinCode={currentJoinCode}
          displayCode={displayCode}
          busy={busy || !connected}
          error={error}
          onCommand={async (command) => {
            await sendCommand(command);
          }}
        />
      );
    }
    if (publicState.phase === "GOLD_SELECTION" && publicState.roundOutcome) {
      return (
        <RoundResults
          outcome={publicState.roundOutcome}
          phase={publicState.phase}
          participants={publicState.participants}
          privateGold={self.goldAwards.reduce((sum, card) => sum + card.value, 0)}
          selfRole={self.role}
          inspectedGoals={self.inspectedGoals}
          pendingGoldChoices={self.pendingGoldChoices}
          onChooseGold={(goldCardId) =>
            void sendCommand({ type: "reward.choose-gold", goldCardId })
          }
          busy={busy}
          error={error}
        />
      );
    }
    if (publicState.phase === "ABANDONED")
      return (
        <SessionEnded
          onLeave={() => {
            void navigate("/", { replace: true });
          }}
        />
      );
    return (
      <PlayerGameScreen
        key={`${projection.public.roomId}:${self.participantId}`}
        projection={projection}
        busy={busy}
        error={error}
        connectionStatus={connectionStatus}
        submitCommand={sendCommand}
      />
    );
  }
  if (normalizedCode === "NEW")
    return <LobbyScreen busy={busy} error={error} onCreateRoom={createRoom} />;
  if (!hasCredential)
    return (
      <LobbyScreen joinCode={normalizedCode} busy={busy} error={error} onJoinRoom={joinRoom} />
    );
  return (
    <main className="centered-page">
      <section className="surface">
        <h1>Restoring your seat…</h1>
        {error ? <p role="alert">{error}</p> : null}
      </section>
    </main>
  );
}
